import { useMemo } from 'react';
import { Badge } from '@/components/UI';
import { FaChartBar } from 'react-icons/fa';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import ParameterActions from '../ParameterActions';
import OptimizationOpportunityCell from '../OptimizationOpportunityCell';
import ParameterSecondaryIdentity from './ParameterSecondaryIdentity';
import { getParameterBaseDisplayLabel, getParameterTrailingWindow } from './helpers';
import type { useParameterPreferences } from '@/hooks/useParameterPreferences';
import type { useScorecardRuleStatus } from '@/hooks/useScorecardRuleStatus';
import type { CanonicalParameterFeatureIdentity, ParameterColumnPreset, ParameterDiscoveryMetric, ParameterTableRow } from './types';

type ParameterPreferencesApi = ReturnType<typeof useParameterPreferences>;
type ScorecardRuleStatusApi = ReturnType<typeof useScorecardRuleStatus>;

export interface UseParameterTableColumnsOptions {
    getCanonicalFeatureValueCount: ScorecardRuleStatusApi['getCanonicalFeatureValueCount'];
    getParameterDisplayLabel: (parameter: {
        path: string;
        canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
    }) => string;
    getStatus: ParameterPreferencesApi['getStatus'];
    isCanonicalValueInScorecard: ScorecardRuleStatusApi['isCanonicalValueInScorecard'];
    isUpdating: boolean;
    openBucketModal: (parameterRow: ParameterTableRow) => void;
    optimizationMode: ParameterDiscoveryMetric;
    parameterColumnPreset: ParameterColumnPreset;
    selectedScorecardIdTop: number | null;
    toggleArchive: ParameterPreferencesApi['toggleArchive'];
    togglePin: ParameterPreferencesApi['togglePin'];
}

export const useParameterTableColumns = ({
    getCanonicalFeatureValueCount,
    getParameterDisplayLabel,
    getStatus,
    isCanonicalValueInScorecard,
    isUpdating,
    openBucketModal,
    optimizationMode,
    parameterColumnPreset,
    selectedScorecardIdTop,
    toggleArchive,
    togglePin
}: UseParameterTableColumnsOptions): ColumnDef<ParameterTableRow>[] => {
    const columns = useMemo<ColumnDef<ParameterTableRow>[]>(
        () => [
            {
                id: 'actions',
                header: '',
                size: 80,
                cell: ({ row }) => (
                    <ParameterActions
                        disabled={isUpdating || !row.original.preferenceIdentity.contextFeatureRef}
                        preferenceIdentity={row.original.preferenceIdentity}
                        status={getStatus(row.original.preferenceIdentity.preferenceKey)}
                        onToggleArchive={toggleArchive}
                        onTogglePin={togglePin}
                    />
                )
            },
            {
                id: 'buckets',
                header: '',
                size: 40,
                cell: ({ row }) => (
                    <button
                        aria-label="Explore custom buckets"
                        className="p-1.5 rounded text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            openBucketModal(row.original);
                        }}
                    >
                        <FaChartBar size={14} />
                    </button>
                )
            },
            {
                id: 'feature',
                accessorFn: (row) => getParameterDisplayLabel(row),
                header: 'Feature',
                size: 220,
                cell: ({ row }) => {
                    const status = getStatus(row.original.preferenceIdentity.preferenceKey);
                    const isPinned = status === 'pinned';
                    const isArchived = status === 'archived';
                    const primaryLabel = getParameterBaseDisplayLabel(row.original);
                    const trailingWindow = getParameterTrailingWindow(row.original);
                    const primaryColorClass = isPinned ? 'text-yellow-400' : isArchived ? 'text-zinc-500' : 'text-white';

                    return (
                        <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-2">
                                <div className={`truncate font-medium ${primaryColorClass}`}>{primaryLabel}</div>
                                {trailingWindow ? (
                                    <span className="shrink-0" title={trailingWindow.description}>
                                        <Badge
                                            pill
                                            text={trailingWindow.badgeLabel}
                                            variant={trailingWindow.kind === 'within' ? 'info' : 'secondary'}
                                        />
                                    </span>
                                ) : null}
                            </div>
                            <ParameterSecondaryIdentity parameter={row.original} />
                        </div>
                    );
                }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionImpactScore' : 'impactScore',
                header: 'Impact Score',
                size: 110,
                cell: ({ row }) => {
                    const score = optimizationMode === 'options' ? row.original.optionImpactScore : row.original.impactScore;

                    if (score == null) return <span className="text-zinc-500">—</span>;

                    // Color gradient: 0-30 red, 30-70 yellow, 70-100 green
                    let colorClass = 'text-red-400';
                    let description = 'Low impact';

                    if (score >= 70) {
                        colorClass = 'text-green-400';
                        description = 'High impact';
                    } else if (score >= 30) {
                        colorClass = 'text-yellow-400';
                        description = 'Moderate impact';
                    }

                    return (
                        <span
                            aria-label={`Impact score: ${score.toFixed(1)} out of 100 (${description})`}
                            className={`font-semibold ${colorClass}`}
                            title={`Impact score: ${score.toFixed(1)}/100 - ${description}`}
                        >
                            {score.toFixed(1)}
                        </span>
                    );
                }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionModelImportance' : 'modelImportance',
                header: 'Model Importance',
                size: 140,
                cell: ({ row }) => {
                    const val = optimizationMode === 'options' ? row.original.optionModelImportance : row.original.modelImportance;

                    const std = optimizationMode === 'options' ? row.original.optionModelImportanceStd : row.original.modelImportanceStd;

                    if (val == null) return <span className="text-zinc-500">—</span>;
                    let colorClass = 'text-red-400';
                    let description = 'Low importance';

                    if (val >= 70) {
                        colorClass = 'text-green-400';
                        description = 'High importance';
                    } else if (val >= 30) {
                        colorClass = 'text-yellow-400';
                        description = 'Moderate importance';
                    }

                    const title =
                        std != null
                            ? `Model importance: ${val.toFixed(1)} / 100 (±${std.toFixed(1)})`
                            : `Model importance: ${val.toFixed(1)} / 100`;

                    return (
                        <span
                            aria-label={`${description}: ${val.toFixed(1)} out of 100`}
                            className={`font-semibold ${colorClass}`}
                            title={title}
                        >
                            {val.toFixed(1)}
                        </span>
                    );
                },
                meta: { tooltip: 'Permutation-based model importance (validation), normalized to 100' }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionOptimizationOpportunity' : 'optimizationOpportunity',
                header: 'Opt. Opportunity',
                size: 130,
                accessorFn: (row) => {
                    const opportunity = optimizationMode === 'options' ? row.optionOptimizationOpportunity : row.optimizationOpportunity;
                    return opportunity?.score ?? -1; // Return -1 for null/undefined so they sort to bottom
                },
                sortFn: (rowA, rowB) => {
                    const scoreA =
                        optimizationMode === 'options'
                            ? (rowA.original.optionOptimizationOpportunity?.score ?? -1)
                            : (rowA.original.optimizationOpportunity?.score ?? -1);

                    const scoreB =
                        optimizationMode === 'options'
                            ? (rowB.original.optionOptimizationOpportunity?.score ?? -1)
                            : (rowB.original.optimizationOpportunity?.score ?? -1);

                    return scoreA - scoreB;
                },
                cell: ({ row }) => {
                    const opportunity =
                        optimizationMode === 'options' ? row.original.optionOptimizationOpportunity : row.original.optimizationOpportunity;

                    return <OptimizationOpportunityCell opportunity={opportunity} />;
                },
                meta: {
                    tooltip:
                        'Value selection opportunity score (0-100) - identifies parameters where choosing specific values can improve performance'
                }
            },
            {
                id: 'model',
                header: 'Model',
                size: 100,
                cell: ({ row }) => {
                    const shape = row.original.adaptive?.shape;
                    if (!shape) return <span className="text-zinc-500">—</span>;
                    const label = shape === 'monotonic' ? 'Monotonic' : shape === 'band' ? 'Band' : 'Ambiguous';

                    const variant: 'success' | 'warning' | 'secondary' =
                        shape === 'monotonic' ? 'success' : shape === 'band' ? 'warning' : 'secondary';

                    return <Badge variant={variant}>{label}</Badge>;
                },
                meta: { tooltip: 'Adaptive assessment model (if available)' }
            },
            {
                accessorKey: 'avgProfit',
                header: 'Avg Profit',
                size: 100,
                cell: ({ row }) => {
                    const profit = row.original.avgProfit;
                    return <span className={profit >= 0 ? 'text-green-400' : 'text-red-400'}>{profit.toFixed(2)}%</span>;
                }
            },
            {
                accessorKey: 'beatSpyRate',
                header: 'Beat SPY%',
                size: 95,
                cell: ({ row }) => {
                    const beatSpy = row.original.beatSpyRate;
                    if (beatSpy == null) return <span className="text-zinc-500">—</span>;
                    return (
                        <span className={`font-semibold ${beatSpy >= 50 ? 'text-green-400' : 'text-red-400'}`}>{beatSpy.toFixed(1)}%</span>
                    );
                }
            },
            {
                accessorKey: 'avgAlpha',
                header: 'Alpha',
                size: 80,
                cell: ({ row }) => {
                    const alpha = row.original.avgAlpha;
                    if (alpha == null) return <span className="text-zinc-500">—</span>;
                    return (
                        <span className={`font-semibold ${alpha > 0 ? 'text-green-400' : alpha < 0 ? 'text-red-400' : 'text-zinc-300'}`}>
                            {alpha > 0 ? '+' : ''}
                            {alpha.toFixed(2)}%
                        </span>
                    );
                }
            },
            {
                accessorKey: 'avgMaxProfit',
                header: 'Avg Max',
                size: 85,
                cell: ({ row }) => {
                    const maxProfit = row.original.avgMaxProfit;
                    if (maxProfit == null) return <span className="text-zinc-500">—</span>;
                    return <span className="text-green-400">{maxProfit.toFixed(2)}%</span>;
                }
            },
            {
                accessorKey: 'avgMaxLoss',
                header: 'Avg Loss',
                size: 85,
                cell: ({ row }) => {
                    const maxLoss = row.original.avgMaxLoss;
                    if (maxLoss == null) return <span className="text-zinc-500">—</span>;
                    return <span className="text-red-400">{maxLoss.toFixed(2)}%</span>;
                }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionLcbScore' : 'lcbScore',
                header: 'LCB',
                size: 80,
                cell: ({ row }) => {
                    const lcb = optimizationMode === 'options' ? row.original.optionLcbScore : row.original.lcbScore;

                    return lcb != null ? <span className="text-zinc-300">{lcb.toFixed(1)}</span> : <span className="text-zinc-500">—</span>;
                },
                meta: {
                    tooltip: 'Lower Confidence Bound - Conservative impact estimate'
                }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionQValue' : 'qValue',
                header: 'q-value',
                size: 80,
                cell: ({ row }) => {
                    const q = optimizationMode === 'options' ? row.original.optionQValue : row.original.qValue;

                    if (q == null) return <span className="text-zinc-500">—</span>;

                    // Highlight significant q-values
                    let colorClass = 'text-zinc-400';
                    let significance = 'Not significant';

                    if (q <= 0.05) {
                        colorClass = 'text-green-400 font-semibold';
                        significance = 'Statistically significant';
                    } else if (q <= 0.1) {
                        colorClass = 'text-yellow-400';
                        significance = 'Marginally significant';
                    }

                    return (
                        <span
                            aria-label={`q-value: ${q.toFixed(3)} (${significance})`}
                            className={colorClass}
                            title={`FDR-adjusted p-value: ${q.toFixed(3)} - ${significance}`}
                        >
                            {q.toFixed(3)}
                        </span>
                    );
                },
                meta: {
                    tooltip: 'FDR-adjusted p-value for statistical significance'
                }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionCoverage' : 'coverage',
                header: 'Coverage',
                size: 90,
                cell: ({ row }) => {
                    const cov = optimizationMode === 'options' ? row.original.optionCoverage : row.original.coverage;

                    return cov != null ? (
                        <span className="text-zinc-300">{(cov * 100).toFixed(1)}%</span>
                    ) : (
                        <span className="text-zinc-500">—</span>
                    );
                },
                meta: {
                    tooltip: 'Percentage of entries with this parameter'
                }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionStability' : 'stability',
                header: 'Stability',
                size: 85,
                cell: ({ row }) => {
                    const stab = optimizationMode === 'options' ? row.original.optionStability : row.original.stability;

                    if (stab == null) return <span className="text-zinc-500">—</span>;

                    // Color based on stability level
                    let colorClass = 'text-red-400';
                    let stabilityLevel = 'Low stability';

                    if (stab >= 0.8) {
                        colorClass = 'text-green-400';
                        stabilityLevel = 'High stability';
                    } else if (stab >= 0.6) {
                        colorClass = 'text-yellow-400';
                        stabilityLevel = 'Moderate stability';
                    }

                    return (
                        <span
                            aria-label={`Stability: ${(stab * 100).toFixed(0)}% (${stabilityLevel})`}
                            className={colorClass}
                            title={`Direction agreement: ${(stab * 100).toFixed(0)}% - ${stabilityLevel}`}
                        >
                            {(stab * 100).toFixed(0)}%
                        </span>
                    );
                },
                meta: {
                    tooltip: 'Direction agreement across time folds'
                }
            },
            {
                accessorKey: 'impact',
                header: 'Impact',
                size: 90,
                cell: ({ row }) => {
                    const { impact } = row.original;
                    return (
                        <Badge variant={impact === 'positive' ? 'success' : impact === 'negative' ? 'danger' : 'secondary'}>{impact}</Badge>
                    );
                }
            },
            {
                accessorKey: optimizationMode === 'options' ? 'optionBestGroup' : 'bestGroup',
                header: 'Best Group',
                size: 180,
                cell: ({ row }) => {
                    const bg = optimizationMode === 'options' ? row.original.optionBestGroup : row.original.bestGroup;

                    if (!bg) return <span className="text-zinc-500">—</span>;

                    return (
                        <span className="text-zinc-300 text-sm">
                            <span className="text-white font-medium">{bg.label}</span>{' '}
                            <span className={bg.lift >= 0 ? 'text-green-400' : 'text-red-400'}>
                                {bg.lift >= 0 ? '+' : ''}
                                {bg.lift.toFixed(2)}%
                            </span>
                            {' (LCB: '}
                            {bg.ciLower.toFixed(2)}%)
                        </span>
                    );
                },
                meta: {
                    tooltip: 'Best performing parameter value group'
                }
            },
            {
                accessorKey: 'uniqueValues',
                header: 'Values',
                size: 90,
                cell: ({ row }) => {
                    const total = row.original.uniqueValues;

                    const inScorecard =
                        selectedScorecardIdTop && row.original.scorecardCandidate
                            ? getCanonicalFeatureValueCount(row.original.scorecardCandidate.serializedContextFeatureRef)
                            : 0;

                    if (inScorecard === 0) {
                        return <span className="text-zinc-400">{total}</span>;
                    }

                    return (
                        <span className="text-zinc-400">
                            {total}{' '}
                            <span
                                className={`font-semibold ${inScorecard === total ? 'text-green-400' : 'text-green-500'}`}
                                title={`${inScorecard} value${inScorecard === 1 ? '' : 's'} in scorecard`}
                            >
                                ({inScorecard})
                            </span>
                        </span>
                    );
                }
            },
            {
                accessorKey: 'sampleSize',
                header: 'Samples',
                size: 85,
                cell: ({ row }) => <span className="text-zinc-400">{row.original.sampleSize}</span>
            }
        ],
        [
            getCanonicalFeatureValueCount,
            getParameterDisplayLabel,
            getStatus,
            isCanonicalValueInScorecard,
            isUpdating,
            openBucketModal,
            optimizationMode,
            selectedScorecardIdTop,
            toggleArchive,
            togglePin
        ]
    );

    const visibleParameterColumns = useMemo(() => {
        if (parameterColumnPreset === 'all') return columns;

        const impactColumn = optimizationMode === 'options' ? 'optionImpactScore' : 'impactScore';
        const importanceColumn = optimizationMode === 'options' ? 'optionModelImportance' : 'modelImportance';
        const opportunityColumn = optimizationMode === 'options' ? 'optionOptimizationOpportunity' : 'optimizationOpportunity';
        const bestGroupColumn = optimizationMode === 'options' ? 'optionBestGroup' : 'bestGroup';
        const lcbColumn = optimizationMode === 'options' ? 'optionLcbScore' : 'lcbScore';
        const qValueColumn = optimizationMode === 'options' ? 'optionQValue' : 'qValue';
        const coverageColumn = optimizationMode === 'options' ? 'optionCoverage' : 'coverage';
        const stabilityColumn = optimizationMode === 'options' ? 'optionStability' : 'stability';

        const coreColumns = new Set([
            'actions',
            'buckets',
            'feature',
            impactColumn,
            importanceColumn,
            opportunityColumn,
            bestGroupColumn,
            'uniqueValues',
            'sampleSize'
        ]);

        const statisticalColumns = new Set([
            ...coreColumns,
            'model',
            'avgProfit',
            lcbColumn,
            qValueColumn,
            coverageColumn,
            stabilityColumn,
            'impact'
        ]);

        const allowedColumns = parameterColumnPreset === 'statistical' ? statisticalColumns : coreColumns;

        return columns.filter((column) => {
            const { id } = column;
            const accessorKey = 'accessorKey' in column ? column.accessorKey : undefined;
            const key = typeof id === 'string' ? id : typeof accessorKey === 'string' ? accessorKey : '';
            return allowedColumns.has(key);
        });
    }, [columns, optimizationMode, parameterColumnPreset]);

    return visibleParameterColumns;
};
